import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Info, ShieldAlert } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/AuthContext";

export default function EmailSettingsPage() {
  const { refresh } = useAuth();
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({
    email_sender_name: "",
    email_sender_address: "",
    email_reply_to: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/company/settings");
    setCompany(data);
    setForm({
      email_sender_name: data.email_sender_name || "",
      email_sender_address: data.email_sender_address || "",
      email_reply_to: data.email_reply_to || "",
    });
  };
  useEffect(() => {
    load();
  }, []);

  if (!company) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/company/settings", form);
      toast.success("Configuration email enregistrée");
      load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleSandbox = async (nextSandbox) => {
    try {
      await api.patch("/company/sandbox-mode", { sandbox_mode: nextSandbox });
      toast.success(nextSandbox ? "Mode test activé" : "Envoi réel activé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const canGoLive = company.can_go_live;

  return (
    <div className="space-y-6" data-testid="email-settings-page">
      <div>
        <h1 className="text-2xl font-bold">Emails</h1>
        <p className="text-sm text-slate-400">
          Configuration de l'expéditeur et du mode d'envoi. Tous les emails passent par Resend.
        </p>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Nom expéditeur</Label>
            <Input
              value={form.email_sender_name}
              onChange={(e) => setForm({ ...form, email_sender_name: e.target.value })}
              placeholder="ToitPro"
              className="border-slate-700 bg-slate-950/40"
              data-testid="sender-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Adresse expéditeur</Label>
            <Input
              type="email"
              value={form.email_sender_address}
              onChange={(e) => setForm({ ...form, email_sender_address: e.target.value })}
              placeholder="notifications@toitpro.be"
              className="border-slate-700 bg-slate-950/40 font-mono"
              data-testid="sender-address-input"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Reply-to</Label>
            <Input
              type="email"
              value={form.email_reply_to}
              onChange={(e) => setForm({ ...form, email_reply_to: e.target.value })}
              placeholder="anna@toitpro.be"
              className="border-slate-700 bg-slate-950/40 font-mono"
              data-testid="reply-to-input"
            />
          </div>
          <div className="sm:col-span-2 flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100/80">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            L&apos;adresse expéditeur nécessite un <b>domaine vérifié</b> (SPF/DKIM) pour l&apos;envoi réel.
            Tant qu&apos;aucun domaine n&apos;est vérifié, l&apos;envoi reste en <b>mode test</b> (aucun email n&apos;est réellement envoyé au destinataire).
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={save}
        disabled={saving}
        className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
        data-testid="email-save-btn"
      >
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Enregistrer
      </Button>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-6">
          <h3 className="mb-3 font-semibold text-slate-200">Mode d'envoi</h3>
          <div className="flex items-center justify-between rounded-lg border border-slate-700 bg-slate-950/40 p-4">
            <div>
              <div className="text-sm font-medium text-slate-100" data-testid="sandbox-status-label">
                {company.sandbox_mode ? "Mode test (aucun envoi réel)" : "Envoi réel activé"}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {company.sandbox_mode
                  ? "Les emails sont simulés et visibles dans le journal des emails."
                  : "Les emails sont réellement envoyés au destinataire."}
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Switch
                      checked={!company.sandbox_mode}
                      onCheckedChange={(v) => toggleSandbox(!v)}
                      disabled={!canGoLive}
                      data-testid="sandbox-toggle"
                    />
                  </span>
                </TooltipTrigger>
                {!canGoLive && (
                  <TooltipContent className="max-w-xs border-slate-700 bg-slate-950 text-slate-200">
                    <p className="flex items-start gap-1.5 text-xs">
                      <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                      Configurer un domaine expéditeur vérifié pour activer
                      l&apos;envoi réel.
                    </p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
